/* global React, Icon, Button, IconButton, Card, Segmented, Modal, Field, Input, toast, t,
   PJ_API, PjDot, PjProjectActivityFields, PjProjectModal, PjActivityModal,
   pjDateFull, pjDateShort, pjDayNames, pjHours, pjTodayIso, pjErrorToast */
// ============================================================
// PLUMME 3 — Prosjekter (under Salg): timeføring, oversikt, innstillinger
//
// Sida orkestrerer bare state og UI. Regnereglene ligger i
// `src/services/projectTimeTracking.js`, lagring og fakturering i
// `src/services/backend.jsx`. Ingen lokal lagring — alt gaar til Firestore.
// ============================================================
const PJ_EMPTY_MODEL = () => ({
  projects: [], activities: [], entries: [], pinnedRows: [],
  settings: { lockedThrough: '', pinnedRows: [] },
  projectsById: new Map(), activitiesById: new Map(),
});
const pjReadModel = () => window.PlummeBackend?.getProjectTimeModel?.() || PJ_EMPTY_MODEL();

// Modellen bygges av backend fra state-dokumentet. Sida abonnerer paa de samme
// hendelsene som resten av appen, slik at timer registrert i en annen fane eller
// paa en annen enhet dukker opp uten reload.
function usePjModel() {
  const [model, setModel] = useState(pjReadModel);
  useEffect(() => {
    const refresh = () => setModel(pjReadModel());
    refresh();
    window.addEventListener('plumme-projects-changed', refresh);
    window.addEventListener('plumme-backend-state', refresh);
    return () => {
      window.removeEventListener('plumme-projects-changed', refresh);
      window.removeEventListener('plumme-backend-state', refresh);
    };
  }, []);
  return model;
}

// Navnet paa den som forer timer. Vises i overskriften slik at det er tydelig
// hvem registreringene tilhorer naar flere bruker samme foretak.
const pjPersonName = () => {
  const backend = window.PlummeBackend;
  return backend?.userProfile?.displayName || backend?.user?.displayName || backend?.user?.email || t('pj.person.fallback', 'deg');
};
// Felles redigeringsdialog for en timeoppforing. Brukes bade for nye timer og
// for endring fra maanedsoversikten og rapportene.
function PjTimeEntryModal({ open, onClose, entry, model, onSaved }) {
  const api = PJ_API();
  const [projectId, setProjectId] = useState(entry.projectId || '');
  const [activityId, setActivityId] = useState(entry.activityId || '');
  const [hours, setHours] = useState(entry.hours ? String(entry.hours) : '');
  const [date, setDate] = useState(entry.date || pjTodayIso());
  const [note, setNote] = useState(entry.note || '');
  const [busy, setBusy] = useState(false);
  const editing = Boolean(entry.id);
  const locked = api.isDateLocked(model.settings, date);
  const invoiced = entry.invoiceStatus === 'invoiced';
  const bump = (delta) => setHours((current) => String(Math.max(0, Math.round(((Number(String(current).replace(',', '.')) || 0) + delta) * 100) / 100)));
  const save = async () => {
    setBusy(true);
    try {
      await window.PlummeBackend.saveTimeEntry({ id: entry.id, projectId, activityId, hours, date, note, personName: pjPersonName() });
      toast(editing ? t('pj.toast.entryUpdated', 'Timeoppføringen er oppdatert') : t('pj.toast.entrySaved', 'Timene er registrert'), 'success');
      onSaved?.();
      onClose();
    } catch (error) {
      pjErrorToast(error, 'pj.error.saveEntry', 'Kunne ikke lagre timeoppføringen.');
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await window.PlummeBackend.deleteTimeEntry(entry.id);
      toast(t('pj.toast.entryDeleted', 'Timeoppføringen er slettet'), 'success');
      onSaved?.();
      onClose();
    } catch (error) {
      pjErrorToast(error, 'pj.error.deleteEntry', 'Kunne ikke slette timeoppføringen.');
    } finally { setBusy(false); }
  };
  return <Modal className="pj-entry-modal" open={open} onClose={onClose} max={560}
    title={editing ? t('pj.entry.editTitle', 'Endre timeoppføring') : t('pj.entry.newTitle', 'Ny timeregistrering')}
    footer={<div className="row between pj-entry-foot" style={{ width: '100%' }}>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <div className="row gap-10 pj-entry-foot-main">
        {editing && <Button variant="secondary" icon="trash" disabled={busy || invoiced} onClick={remove}>{t('common.delete', 'Slett')}</Button>}
        <Button icon="check" disabled={busy || invoiced || locked || !projectId || !activityId} onClick={save}>{busy ? t('common.saving') : t('common.save')}</Button>
      </div>
    </div>}>
    <div className="col gap-14">
      {invoiced && <div className="alert alert-info"><span className="alert-ico"><Icon name="lock" size={18} /></span><span>{t('pj.entry.invoicedLocked', 'Timene er fakturert og kan ikke endres. Slett eller krediter fakturautkastet for å frigjøre dem.')}</span></div>}
      {locked && !invoiced && <div className="alert alert-warning"><span className="alert-ico"><Icon name="alertTri" size={18} /></span><span>{t('pj.entry.periodLocked', 'Timeføringen for denne perioden er markert som ferdig.')}</span></div>}
      <PjProjectActivityFields model={model} projectId={projectId} activityId={activityId}
        includeClosed={editing}
        onChange={(next) => { setProjectId(next.projectId); setActivityId(next.activityId); }} />
      <Field label={t('pj.field.hours', 'Antall timer')} help={t('helpTopics.hours.body')} helpTitle={t('helpTopics.hours.title')}>
        <div className="row gap-10 wrap" style={{ alignItems: 'center' }}>
          <Input style={{ width: 100 }} inputMode="decimal" value={hours} onChange={(event) => setHours(event.target.value)} />
          <Button variant="secondary" size="sm" onClick={() => bump(1)}>+ 1</Button>
          <Button variant="secondary" size="sm" onClick={() => bump(0.5)}>+ ½</Button>
          <Button variant="secondary" size="sm" onClick={() => bump(-0.5)}>− ½</Button>
          <Button variant="secondary" size="sm" onClick={() => bump(-1)}>− 1</Button>
        </div>
      </Field>
      <Field label={t('pj.field.date', 'Dato')}><Input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
      <Field label={t('pj.field.description', 'Beskrivelse')}>
        <textarea className="input" rows={3} value={note} onChange={(event) => setNote(event.target.value)}
          placeholder={t('pj.field.descriptionHint', 'Hva gikk timene til? Teksten følger med på fakturaen.')} />
      </Field>
    </div>
  </Modal>;
}

// ---------- Timeføring ----------
function PjTimeSheetCell({ entry, date, row, locked }) {
  const api = PJ_API();
  const [draft, setDraft] = useState(entry ? String(entry.hours) : '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(entry ? String(entry.hours) : ''); }, [entry ? entry.id + ':' + entry.hours : '']);
  const invoiced = entry?.invoiceStatus === 'invoiced';
  // Lagring skjer paa blur, ikke paa hvert tastetrykk. Ett skriv per celle i
  // stedet for ett per tegn.
  const commit = async () => {
    const raw = String(draft).trim();
    const next = raw === '' ? 0 : api.roundHours(raw);
    const current = entry ? entry.hours : 0;
    if (next === current) return;
    setBusy(true);
    try {
      if (!next && entry) await window.PlummeBackend.deleteTimeEntry(entry.id);
      else if (next) await window.PlummeBackend.saveTimeEntry({ id: entry?.id, projectId: row.projectId, activityId: row.activityId, hours: next, date, note: entry?.note || '', personName: pjPersonName() });
    } catch (error) {
      setDraft(entry ? String(entry.hours) : '');
      pjErrorToast(error, 'pj.error.saveEntry', 'Kunne ikke lagre timeoppføringen.');
    } finally { setBusy(false); }
  };
  return <Input className="input pj-hour-input" inputMode="decimal" value={draft} disabled={locked || invoiced || busy}
    title={invoiced ? t('pj.entry.invoicedShort', 'Fakturert') : undefined}
    onChange={(event) => setDraft(event.target.value)} onBlur={commit}
    onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />;
}

function PjTimeforingTab({ model }) {
  const api = PJ_API();
  const [monday, setMonday] = useState(() => api.mondayOf(new Date()));
  const [selDay, setSelDay] = useState(() => pjTodayIso());
  const [view, setView] = useState('uke');
  // Under 1120px er ukevelgeren en skuff som spretter opp fra bunnen, styrt av
  // kalenderknappen pa hoyre skjermkant. Over 1120px er den en vanlig rad, og
  // denne tilstanden har ingen virkning.
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newRow, setNewRow] = useState({ projectId: '', activityId: '' });
  const [newEntry, setNewEntry] = useState(null);
  const [quickCreate, setQuickCreate] = useState('');
  const days = api.weekDays(monday);
  const week = api.isoWeekNumber(monday);
  const rows = model.pinnedRows.map((row) => ({
    ...row,
    project: model.projectsById.get(row.projectId),
    activity: model.activitiesById.get(row.activityId),
  }));
  const entryFor = (projectId, activityId, date) =>
    model.entries.find((entry) => entry.projectId === projectId && entry.activityId === activityId && entry.date === date);
  const rowTotal = (row) => api.totalHours(days.map((date) => entryFor(row.projectId, row.activityId, date)).filter(Boolean));
  const dayTotal = (date) => api.totalHours(model.entries.filter((entry) => entry.date === date));
  const weekTotal = api.totalHours(api.entriesInRange(model.entries, days[0], days[6]));
  const locked = api.isDateLocked(model.settings, days[6]);
  const dayEntries = model.entries.filter((entry) => entry.date === selDay).map((entry) => api.decorateEntry(entry, model));

  const savePinned = async (nextRows) => {
    try { await window.PlummeBackend.saveTimeTrackingSettings({ pinnedRows: nextRows }); }
    catch (error) { pjErrorToast(error, 'pj.error.saveRows', 'Kunne ikke oppdatere radene.'); }
  };
  const addRow = () => {
    if (!newRow.projectId || !newRow.activityId) { toast(t('pj.row.pickBoth', 'Velg prosjekt og aktivitet'), 'warning'); return; }
    void savePinned([...model.pinnedRows, { id: `pr${Date.now()}`, ...newRow }]);
    setAddOpen(false);
    setNewRow({ projectId: '', activityId: '' });
  };
  const setLock = async (lockedThrough) => {
    try {
      await window.PlummeBackend.saveTimeTrackingSettings({ lockedThrough });
      toast(lockedThrough
        ? t('pj.lock.done', 'Timeføringen er markert som ferdig til og med uke {week}', { week })
        : t('pj.lock.undone', 'Timeføringen er åpnet igjen'), 'success');
    } catch (error) { pjErrorToast(error, 'pj.error.lock', 'Kunne ikke oppdatere låsen.'); }
  };

  return <div>
    <h2 className="t-h1" style={{ marginBottom: 18 }} data-help={t('helpTopics.hours.body')} data-help-title={t('helpTopics.hours.title')}>{t('pj.time.title', 'Timeføring for {name}', { name: pjPersonName() })}</h2>
    <div className="row between wrap gap-12" style={{ marginBottom: 16 }}>
      <div className={'row gap-8 pj-week-nav' + (weekPickerOpen ? ' open' : '')}>
        <Button variant="secondary" size="sm" aria-label={t('pj.week.back', 'Tre uker tilbake')} onClick={() => setMonday(api.addDays(monday, -21))}>
          <Icon name="chevLeft" size={15} className="pj-ico-desktop" /><Icon name="chevUp" size={15} className="pj-ico-mobile" /></Button>
        {[-1, 0, 1, 2].map((offset) => {
          const start = api.addDays(monday, offset * 7);
          // Hele merkelappen ligger i aria-label. I skuffen er det bare plass til
          // uketallet, sa ordet «Uke» og datointervallet skjules visuelt — men
          // knappen heter fortsatt det samme for en skjermleser.
          return <button key={offset} className={'btn btn-secondary' + (offset === 0 ? ' pj-week-active' : '')}
            aria-label={t('pj.week.label', 'Uke {week}', { week: api.isoWeekNumber(start) })}
            onClick={() => { setMonday(start); setWeekPickerOpen(false); }}>
            <div className="col" style={{ alignItems: 'center', lineHeight: 1.3 }}>
              <b><span className="pj-week-label">{t('pj.week.word', 'Uke')} </span>{api.isoWeekNumber(start)}</b>
              <span className="t-xs muted pj-week-date">{pjDateShort(start)} – {pjDateShort(api.addDays(start, 6))}</span>
            </div></button>;
        })}
        <Button variant="secondary" size="sm" aria-label={t('pj.week.forward', 'Tre uker fram')} onClick={() => setMonday(api.addDays(monday, 21))}>
          <Icon name="chevRight" size={15} className="pj-ico-desktop" /><Icon name="chevDown" size={15} className="pj-ico-mobile" /></Button>
      </div>
      <div className="row gap-8">
        <Button variant="secondary" size="sm" icon="calendar" onClick={() => { setMonday(api.mondayOf(new Date())); setSelDay(pjTodayIso()); }}>{t('pj.week.current', 'Denne uken')}</Button>
        <Segmented soft value={view} onChange={setView} options={[{ value: 'dag', label: t('pj.view.day', 'Dag') }, { value: 'uke', label: t('pj.view.week', 'Uke') }]} />
      </div>
    </div>
    {/* Bare synlig under 1120px — CSS-en styrer det, ikke en bredde-lytter her. */}
    {weekPickerOpen && <div className="pj-week-scrim" onClick={() => setWeekPickerOpen(false)} />}
    <button className="pj-week-fab" onClick={() => setWeekPickerOpen((open) => !open)}
      aria-label={t('pj.week.pick', 'Velg uke')} title={t('pj.week.pick', 'Velg uke')}><Icon name="calendar" size={20} /></button>
    <div className="col gap-18">
      <Card className="pj-day-card">
        <b className="t-h3 pj-day-card-date">{pjDateFull(selDay)}</b>
        <div className="pj-day-card-row">
          <div className="pj-day-card-content">{dayEntries.length === 0
            ? <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('pj.day.empty', 'Ingen timer registrert denne dagen.')}</div>
            : <div className="col gap-8">{dayEntries.map((entry) => <div data-app-control="true" key={entry.id} className="chatline" style={{ cursor: 'pointer' }} onClick={() => setNewEntry(entry)}>
              <PjDot color={entry.project?.color} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t-h3" style={{ fontSize: 13.5 }}>{entry.project?.name} / {entry.activity?.name}</div>
                {entry.note && <div className="t-sm muted">{entry.note}</div>}
              </div>
              <b className="t-num">{pjHours(entry.hours)}</b>
            </div>)}</div>}</div>
          <div className="pj-day-actions">
            <Button variant="secondary" size="sm" icon="plus" onClick={() => setQuickCreate('project')} data-help={t('helpTopics.project.body')} data-help-title={t('helpTopics.project.title')}>{t('pj.project.newShort', 'Nytt prosjekt')}</Button>
            <Button variant="secondary" size="sm" icon="plus" disabled={!model.projects.length} onClick={() => setQuickCreate('activity')} data-help={t('helpTopics.activity.body')} data-help-title={t('helpTopics.activity.title')}>{t('pj.activity.newTitle', 'Ny aktivitet')}</Button>
            <Button size="sm" icon="plus" onClick={() => setNewEntry({ date: selDay, projectId: '', activityId: '', hours: '', note: '' })} data-help={t('helpTopics.hours.body')} data-help-title={t('helpTopics.hours.title')}>{t('pj.entry.newTitle', 'Ny timeregistrering')}</Button>
          </div>
        </div>
      </Card>
      {view === 'uke' && <Card pad={false}>
        <div className="tbl-wrap" style={{ border: 'none' }}>
          <table className="tbl pj-week-tbl">
            <thead><tr><th>{t('pj.table.projectActivity', 'Prosjekt / aktivitet')}</th>
              {days.map((date, index) => <th data-app-control="true" key={date} className="pj-day-head" aria-current={date === selDay ? 'date' : undefined} onClick={() => setSelDay(date)}>
                {pjDayNames()[index]}<div className="pj-day-date">{pjDateShort(date)}</div></th>)}
              <th style={{ textAlign: 'right' }}>{t('pj.table.sum', 'Sum')}</th></tr></thead>
            <tbody>
              {rows.map((row) => <tr key={row.id}>
                <td><div className="row gap-10">
                  <PjDot color={row.project.color} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-h3" style={{ fontSize: 13.5 }}>{row.project.name}</div>
                    <div className="t-sm" style={{ color: 'var(--plum-700)', fontWeight: 700 }}>{row.activity.name}</div>
                  </div>
                  <IconButton name="x" size="sm" aria-label={t('pj.row.remove', 'Fjern raden')}
                    onClick={() => savePinned(model.pinnedRows.filter((entry) => entry.id !== row.id))} />
                </div></td>
                {days.map((date) => <td key={date} style={{ textAlign: 'center' }}>
                  <PjTimeSheetCell entry={entryFor(row.projectId, row.activityId, date)} date={date} row={row}
                    locked={api.isDateLocked(model.settings, date)} />
                </td>)}
                <td className="t-sm muted num">{pjHours(rowTotal(row))}</td>
              </tr>)}
              <tr><td>{addOpen
                ? <div className="row gap-8 wrap" style={{ padding: '6px 0' }}>
                  <PjProjectActivityFields compact model={model} projectId={newRow.projectId} activityId={newRow.activityId} onChange={setNewRow} />
                  <Button size="sm" onClick={addRow} data-help={t('helpTopics.hours.body')} data-help-title={t('helpTopics.hours.title')}>{t('pj.row.add', 'Legg til')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button>
                </div>
                : <Button variant="secondary" size="sm" icon="plus" onClick={() => setAddOpen(true)}>{t('pj.row.new', 'Ny rad')}</Button>}</td>
                {days.map((date) => <td key={date} className="t-sm muted" style={{ textAlign: 'center' }}>{pjHours(dayTotal(date))}</td>)}
                <td className="num"><b>{pjHours(weekTotal)}</b></td></tr>
            </tbody>
          </table>
        </div>
        <div className="row between" style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 10 }}>
          <span className="t-sm muted" style={{ fontWeight: 700 }}>{locked
            ? t('pj.lock.state.done', 'Timeføringen er registrert som ferdig til og med {date}.', { date: pjDateFull(model.settings.lockedThrough) })
            : t('pj.lock.state.open', 'Timeføringen er ikke registrert som ferdig')}</span>
          {locked
            ? <Button variant="secondary" size="sm" onClick={() => setLock('')} data-help={t('helpTopics.weekLock.body')} data-help-title={t('helpTopics.weekLock.title')}>{t('pj.lock.undo', 'Angre')}</Button>
            : <Button variant="secondary" size="sm" onClick={() => setLock(days[6])} data-help={t('helpTopics.weekLock.body')} data-help-title={t('helpTopics.weekLock.title')}>{t('pj.lock.markWeek', 'Marker som ferdig til og med uke {week}', { week })}</Button>}
        </div>
      </Card>}
    </div>
    {newEntry && <PjTimeEntryModal open entry={newEntry} model={model} onClose={() => setNewEntry(null)} />}
    {quickCreate === 'project' && <PjProjectModal open project={{}} model={model} onClose={() => setQuickCreate('')} />}
    {quickCreate === 'activity' && <PjActivityModal open activity={{}} model={model} onClose={() => setQuickCreate('')} />}
  </div>;
}

function ProsjekterPage({ setRoute }) {
  const model = usePjModel();
  const consumeSearchTarget = () => { const target = window.__plummeProjectSearchTarget || null; window.__plummeProjectSearchTarget = null; return target; };
  const [initialSearchTarget] = useState(consumeSearchTarget);
  const [searchTarget, setSearchTarget] = useState(initialSearchTarget);
  const [tab, setTab] = useState(initialSearchTarget ? 'innst' : 'time');
  const [assistantDraft, setAssistantDraft] = useState(null);
  const OversiktTab = window.PjOversiktTab;
  const InnstillingerTab = window.PjInnstillingerTab;
  useEffect(() => {
    const openSearchTarget = () => {
      const target = consumeSearchTarget();
      if (!target) return;
      setSearchTarget(target);
      setTab('innst');
    };
    window.addEventListener('plumme-global-search-open', openSearchTarget);
    return () => window.removeEventListener('plumme-global-search-open', openSearchTarget);
  }, []);
  useProjectPageControlAdapter({
    model,tab,setTab,draft:assistantDraft,setDraft:setAssistantDraft,
    isLocked:(date)=>PJ_API().isDateLocked(model.settings,date),
    saveProject:(fields)=>window.PlummeBackend.saveProjectRecord({
      id:fields.id,name:fields.name,projectNumber:fields.projectNumber,customerId:fields.customerId,
      contactPerson:fields.contactPerson,startDate:fields.startDate,endDate:fields.endDate,
      description:fields.description,color:fields.color,status:fields.status,
    }),
    saveTimeEntry:(fields)=>window.PlummeBackend.saveTimeEntry({
      id:fields.id,projectId:fields.projectId,activityId:fields.activityId,hours:fields.hours,
      date:fields.date,note:fields.note,personName:pjPersonName(),
    }),
    deleteTimeEntry:(id)=>window.PlummeBackend.deleteTimeEntry(id),
    createInvoiceDraft:(input)=>window.PlummeBackend.createInvoiceDraftFromTimeEntries(input),
    openInvoice:(invoice)=>{window.PlummeBackend.setOpenInvoice?.(invoice);setRoute?.('faktura');},
  });
  return <div>
    <div className="row between" style={{ marginBottom: 18 }}>
      <Segmented value={tab} onChange={setTab} options={[
        { value: 'time', label: t('pj.tab.time', 'Timeføring') },
        { value: 'oversikt', label: t('pj.tab.overview', 'Oversikt') },
        { value: 'innst', label: t('pj.tab.settings', 'Innstillinger') },
      ]} />
    </div>
    {tab === 'time' && <PjTimeforingTab model={model} />}
    {tab === 'oversikt' && OversiktTab && <OversiktTab model={model} setRoute={setRoute} />}
    {tab === 'innst' && InnstillingerTab && <InnstillingerTab model={model} searchTarget={searchTarget} />}
    {assistantDraft?.kind==='project'&&<PjProjectModal open project={assistantDraft.fields} model={model} onClose={()=>setAssistantDraft(null)} onSaved={()=>setAssistantDraft(null)}/>}
    {assistantDraft?.kind==='time_entry'&&<PjTimeEntryModal open entry={assistantDraft.fields} model={model} onClose={()=>setAssistantDraft(null)} onSaved={()=>setAssistantDraft(null)}/>}
  </div>;
}

Object.assign(window, {
  ProsjekterPage,
  PjTimeEntryModal,
});
window.dispatchEvent(new Event('plumme-page-registered'));
